import { Reducer } from 'redux';
import { AppThunkAction } from './';
import { AuditLogApi, AuditLogItem, AuditLogQuery } from './services/auditLogService';
import { CancelTokenSource } from 'axios';
import * as analytics from '../analytics';

export interface AuditLogsState {
    items: AuditLogItem[];
    canLoadMore: boolean;
    isLoading: boolean;
    query: AuditLogQuery;
    error?: string;
}

const unloadedState: AuditLogsState = {
    items: [],
    canLoadMore: false,
    isLoading: true,
    query: { take: 100, searchMode: 'exact' },
};

interface LoadAuditLogsAction { type: 'LOAD_AUDIT_LOGS'; query: AuditLogQuery }
interface AuditLogsLoadedAction { type: 'AUDIT_LOGS_LOADED'; items: AuditLogItem[]; canLoadMore: boolean; }
interface AuditLogsAppendedAction { type: 'AUDIT_LOGS_APPENDED'; items: AuditLogItem[]; canLoadMore: boolean; }
interface AuditLogsFailedAction { type: 'AUDIT_LOGS_FAILED'; error: string }
interface SetAuditLogsQueryAction { type: 'SET_AUDIT_LOGS_QUERY'; patch: Partial<AuditLogQuery> }
interface ClearAuditLogsAction { type: 'CLEAR_AUDIT_LOGS' }

export type KnownAction =
    | LoadAuditLogsAction
    | AuditLogsLoadedAction
    | AuditLogsAppendedAction
    | AuditLogsFailedAction
    | SetAuditLogsQueryAction
    | ClearAuditLogsAction;

// Track active requests to cancel them when new queries are loaded
let activeItemsRequest: CancelTokenSource | null = null;

export const actionCreators = {
    setQuery: (patch: Partial<AuditLogQuery>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        dispatch({ type: 'SET_AUDIT_LOGS_QUERY', patch });
    },
    clear: (): AppThunkAction<KnownAction> => (dispatch) => {
        // Cancel any active requests
        if (activeItemsRequest) {
            activeItemsRequest.cancel('Query cleared');
            activeItemsRequest = null;
        }
        dispatch({ type: 'CLEAR_AUDIT_LOGS' });
    },
    load: (override?: Partial<AuditLogQuery>): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const base = state.auditLogs?.query ?? unloadedState.query;
        const query: AuditLogQuery = { ...base, ...override, skip: 0 };

        // Track audit log text search usage
        if (query.search && query.search.trim().length > 0) {
            const user = state.user;
            analytics.trackEvent("Audit Log: Text Search", user, {
                searchText: query.search,
                searchMode: query.searchMode ?? 'exact',
                searchTextLength: query.search.length,
                hasEntityFilter: !!query.entityIds && query.entityIds.length > 0,
                hasEntityTypeFilter: !!query.entityTypes && query.entityTypes.length > 0,
                hasDateFilter: !!(query.from || query.to)
            });
        }

        // Cancel previous requests if they exist
        if (activeItemsRequest) {
            activeItemsRequest.cancel('New query started');
            activeItemsRequest = null;
        }

        dispatch({ type: 'LOAD_AUDIT_LOGS', query });

        const itemsWrapper = AuditLogApi.getAll(query);
        activeItemsRequest = itemsWrapper.cancelTokenSource;

        try {
            const items = await itemsWrapper.promise;
            if (activeItemsRequest === itemsWrapper.cancelTokenSource) {
                activeItemsRequest = null;
            }

            // Determine if more items can be loaded: if we got exactly 'take' items, there might be more
            const take = query.take ?? 100;
            const canLoadMore = items.length === take;

            dispatch({ type: 'AUDIT_LOGS_LOADED', items, canLoadMore });
        } catch (e: any) {
            if (activeItemsRequest === itemsWrapper.cancelTokenSource) {
                activeItemsRequest = null;
            }

            // Don't dispatch error if request was cancelled
            if (e?.message !== 'New query started' && e?.message !== 'Query cleared') {
                dispatch({ type: 'AUDIT_LOGS_FAILED', error: e?.message ?? 'Failed to load audit logs' });
            }
        }
    },
    loadMore: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const base = state.auditLogs?.query ?? unloadedState.query;
        const currentCount = state.auditLogs?.items?.length ?? 0;
        const query: AuditLogQuery = { ...base, skip: currentCount };

        // Avoid loading more when already loading to prevent duplicate requests
        if (state.auditLogs?.isLoading) {
            return;
        }

        dispatch({ type: 'LOAD_AUDIT_LOGS', query });
        try {
            const items = await AuditLogApi.getAll(query).promise;

            // Deduplicate items to prevent duplicate key errors when new records appear during scrolling
            const existingIds = new Set(state.auditLogs?.items?.map(item => item.id) ?? []);
            const newItems = items.filter(item => !existingIds.has(item.id));

            // Determine if more items can be loaded
            const take = query.take ?? 100;
            const canLoadMore = items.length === take;

            dispatch({ type: 'AUDIT_LOGS_APPENDED', items: newItems, canLoadMore });
        } catch (e: any) {
            dispatch({ type: 'AUDIT_LOGS_FAILED', error: e?.message ?? 'Failed to load audit logs' });
        }
    },
};

export const reducer: Reducer<AuditLogsState> = (state: AuditLogsState | undefined, incomingAction: KnownAction): AuditLogsState => {
    const action = incomingAction as KnownAction;
    state = state || unloadedState;
    switch (action.type) {
        case 'SET_AUDIT_LOGS_QUERY':
            return { ...state, query: { ...state.query, ...action.patch } };
        case 'CLEAR_AUDIT_LOGS':
            return { ...unloadedState };
        case 'LOAD_AUDIT_LOGS':
            return { ...state, isLoading: true, error: undefined, query: action.query };
        case 'AUDIT_LOGS_LOADED':
            return { ...state, isLoading: false, items: action.items, canLoadMore: action.canLoadMore };
        case 'AUDIT_LOGS_APPENDED':
            return { ...state, isLoading: false, items: [...state.items, ...action.items], canLoadMore: action.canLoadMore };
        case 'AUDIT_LOGS_FAILED':
            return { ...state, isLoading: false, error: action.error };
        default:
            return state;
    }
};
